import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { LimitProgressDialogComponent } from '@app/work-order/objects/report-progress-footer/limit-progress-dialog/limit-progress-dialog.component';
import { first, take, takeUntil } from 'rxjs/operators';
import { StatusSelectorDialogComponent } from '@app/work-order/objects/report-progress-footer/status-selector-dialog/status-selector-dialog.component';
import { StatusCodeDto } from '@app/generated/model';
import { MatDialog } from '@angular/material/dialog';
import { StatusSelectorDialogData } from '@app/work-order/objects/report-progress-footer/status-selector-dialog/status-selector-dialog';
import { Observable, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { getSelectedObjects, getSelectedWorkLines } from '@app/core/store/selectors/work-order.selector';
import { WorkLineModel, WorkOrderObjectModel } from '@app/work-order/objects/work-order-object.model';
import { DeselectAllObjects, UpdateWorkLineProgress } from '@dwo/actions';

@Component({
  selector: 'app-report-progress-footer',
  templateUrl: './report-progress-footer.component.html',
  styleUrls: ['./report-progress-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ReportProgressFooterComponent implements OnInit, OnDestroy {
  public selectStatusCodeList$!: Observable<StatusCodeDto[]>;
  public highestSelectedProgress$!: Observable<number>;
  public lockedProgress$!: Observable<number>;

  public reportProgress: number | null = null;
  public selectedStatusCode?: StatusCodeDto;
  private selectedObjects: WorkOrderObjectModel[] = [];
  private selectedWorkLines: WorkLineModel[] = [];

  @Output() destroyingElement = new EventEmitter();

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.store.select(getSelectedObjects).subscribe((selectedObjects) => {
      if (selectedObjects.length > 0) {
        this.selectedObjects = selectedObjects;
      } else {
        this.selectedObjects = [];
      }
    });

    this.store.select(getSelectedWorkLines).subscribe((selectedWorkLines) => {
      if (selectedWorkLines.length > 0) {
        this.selectedWorkLines = selectedWorkLines;
        this.setInitialValueForProgress();
      } else {
        this.selectedWorkLines = [];
      }
    });
  }

  setInitialValueForProgress() {
    this.reportProgress = this.getMaxProgress() | 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyingElement.emit();
  }

  public openProgressSlider() {
    this.dialog
      .open(LimitProgressDialogComponent, {
        data: {
          progress: this.reportProgress,
          maxLockedProgress: this.getMaxProgress(),
        },
      })
      .afterClosed()
      .pipe(first())
      .subscribe((result) => {
        if (result) {
          this.reportProgress = result.progress;
        }
      });
  }

  public openStatusSelector(): void {
    this.selectStatusCodeList$.pipe(take(1)).subscribe((statusCodes) => {
      this.dialog
        .open(StatusSelectorDialogComponent, {
          data: {
            selected: this.selectedStatusCode,
            options: [{ name: 'None', id: 'none' }, ...statusCodes],
          } as StatusSelectorDialogData,
        })
        .afterClosed()
        .pipe(first())
        .subscribe((statusCode: StatusCodeDto) => {
          if (statusCode === null) {
            this.setReportObjectStatusId(null);
          } else if (statusCode) {
            this.setReportObjectStatusId(statusCode);
          }
        });
    });
  }

  private setReportObjectStatusId(statusCode: StatusCodeDto | null) {
    this.selectedStatusCode = statusCode ?? undefined;
    if (this.selectedStatusCode) {
      if (this.selectedStatusCode.defaultProgress) {
        this.reportProgress = Math.max(this.selectedStatusCode.defaultProgress, this.getMaxProgress());
      }
    } else {
      //  this.reportProgress = this.workOrderObjectService.getMaxLockedProgress();
    }
  }

  public canSubmit() {
    return (
      this.reportProgress !== undefined //&& this.workOrderObjectService.getMaxLockedProgress() <= this.reportProgress
    );
  }

  public submit() {
    const workLineIds = this.selectedWorkLines
      .map((wl) => wl.id)
      .filter((workLineId): workLineId is string => typeof workLineId === 'string');

    if (this.reportProgress === null) {
      return;
    }

    this.store.dispatch(new UpdateWorkLineProgress(workLineIds, this.reportProgress));
    this.reportProgress = null;
    this.selectedStatusCode = undefined;
    this.store.dispatch(new DeselectAllObjects());
    // this.workOrderObjectService
    //   .updateWorkOrderObjectProgress(
    //     this.selectedStatusCode?.id === 'none' ? null : this.selectedStatusCode?.id,
    //     this.reportProgress,
    //     this.selectedStatusCode?.id === 'none',
    //   )
    //   .then(
    //     () => {
    //       this.reportProgress = null;
    //       this.selectedStatusCode = undefined;
    //       this.store.dispatch(new DeselectAllObjects());
    //     },
    //     (err) => {
    //       throw new Error('An error was thrown while confirming status update: ' + err.message);
    //     },
    //   );
  }

  public cancel() {
    this.reportProgress = null;
    this.selectedStatusCode = undefined;
    this.store.dispatch(new DeselectAllObjects());
  }

  getMaxProgress() {
    return Math.max(...[...this.selectedWorkLines.values()].map((c) => c.progress ?? 0), 0);
  }
}
